"use client";

import { ArrowPathIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { animated, useSpring } from "@react-spring/web";

import { Translation } from "@/data/lang";
import { useLang } from "@/hooks/use-lang";

import {
  ProcessingStatus,
  ProcessingStatusData,
} from "../../services/chat-stream";

const StatusIconMap: Partial<Record<ProcessingStatus, JSX.Element>> = {
  processing: (
    <ArrowPathIcon width={18} height={18} className="text-zinc-500" />
  ),
  done: <CheckCircleIcon width={18} height={18} className="text-green-600" />,
};

export const StatusDisplay = ({
  processingStatus,
}: {
  processingStatus: ProcessingStatusData[];
}) => {
  const { t } = useLang();
  const rotateAnimation = useSpring({
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
    config: { duration: 1000 },
    loop: true,
  });

  return (
    <div className="space-y-4">
      {processingStatus
        .filter(({ status }) => status !== "pending")
        .map(({ name, status, percentage }, index) => (
          <div key={index} className="flex items-center gap-4">
            {/* icon */}
            {status === "processing" ? (
              <animated.div style={rotateAnimation}>
                {StatusIconMap[status]}
              </animated.div>
            ) : (
              StatusIconMap[status]
            )}
            {/* text */}
            <p className="text-sm text-zinc-500">
              {transformNameWithStatus({ t, status, name })}{" "}
              {typeof percentage === "number" ? `(${percentage}%)` : ""}
            </p>
          </div>
        ))}
    </div>
  );
};

const transformNameWithStatus = ({
  t,
  status,
  name,
}: {
  t: Translation;
  status: ProcessingStatus;
  name: string;
}) => {
  const statusText = t.winwingpt.processingStatus[name] || name;

  switch (status) {
    case "processing":
      return `${statusText}${t.winwingpt.processing}`;
    case "done":
      return `${statusText}${t.winwingpt.done}`;
    default:
      return name;
  }
};
